// /utils/reports/ames/xlsxReportGenerator.js

/**
 * Session Report Excel Generator
 * Generates professional engineering reports in XLSX format using ExcelJS
 * Each section is placed on a separate worksheet
 */

import ExcelJS from 'exceljs';

// ============================================================================
// CONSTANTS & STYLING
// ============================================================================

const COLORS = {
  headerBg: 'FFE8E8E8',
  headerText: 'FF333333',
  failBg: 'FFFFE8E8',
  passBg: 'FFE8FFE8',
  warnBg: 'FFFFF8E8',
  borderColor: 'FFCCCCCC',
  titleText: 'FF333333',
  subtitleText: 'FF666666',
};

const FONTS = {
  title: { name: 'Arial', size: 18, bold: true, color: { argb: COLORS.titleText } },
  subtitle: { name: 'Arial', size: 14, color: { argb: COLORS.subtitleText } },
  heading: { name: 'Arial', size: 12, bold: true, color: { argb: COLORS.headerText } },
  header: { name: 'Arial', size: 10, bold: true },
  normal: { name: 'Arial', size: 10 },
  small: { name: 'Arial', size: 9, color: { argb: 'FF999999' } },
};

const BORDER_STYLE = {
  bottom: { style: 'thin', color: { argb: COLORS.borderColor } },
};

// ============================================================================
// HELPERS
// ============================================================================

function formatStation(feet) {
  if (feet === null || feet === undefined) return '--';
  const stations = Math.floor(feet / 100);
  const remainder = (feet % 100).toFixed(1);
  return `${stations}+${remainder.padStart(4, '0')}`;
}

function formatDistance(feet) {
  if (feet === null || feet === undefined) return '--';
  if (feet >= 5280) return `${(feet / 5280).toFixed(2)} mi`;
  return `${feet.toLocaleString()} ft`;
}

function formatDate(dateString) {
  if (!dateString) return '--';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  } catch {
    return dateString;
  }
}

function formatNumber(num, decimals = 2) {
  if (num === null || num === undefined || isNaN(num)) return '--';
  return Number(num).toFixed(decimals);
}

function applyHeaderStyle(row) {
  row.eachCell(cell => {
    cell.font = FONTS.header;
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.headerBg } };
    cell.border = BORDER_STYLE;
    cell.alignment = { vertical: 'middle' };
  });
}

function applyDataRowStyle(row) {
  row.eachCell(cell => {
    cell.font = FONTS.normal;
    cell.border = BORDER_STYLE;
    cell.alignment = { vertical: 'middle' };
  });
}

function setColumnWidths(sheet, widths) {
  widths.forEach((width, i) => {
    sheet.getColumn(i + 1).width = width;
  });
}

function addTitleRow(sheet, title, rowNum = 1) {
  const row = sheet.getRow(rowNum);
  row.getCell(1).value = title;
  row.getCell(1).font = FONTS.heading;
  row.height = 20;
  return rowNum + 1;
}

// ============================================================================
// SHEET GENERATORS
// ============================================================================

function generateSummarySheet(workbook, session, project, projectConfig, segments, events, isReversed) {
  const sheet = workbook.addWorksheet('Summary');
  const meta = session?.meta || {};

  // Title
  sheet.getRow(1).getCell(1).value = 'SESSION ANALYSIS REPORT';
  sheet.getRow(1).getCell(1).font = FONTS.title;
  sheet.getRow(2).getCell(1).value = session?.name || session?.filename || 'Untitled Session';
  sheet.getRow(2).getCell(1).font = FONTS.subtitle;
  sheet.getRow(3).height = 10;

  // Session Info Section
  let row = 5;
  sheet.getRow(row).getCell(1).value = 'Session Information';
  sheet.getRow(row).getCell(1).font = FONTS.heading;
  sheet.getRow(row).getCell(4).value = 'Equipment';
  sheet.getRow(row).getCell(4).font = FONTS.heading;
  row++;

  const infoData = [
    ['Operator', session?.operator || meta.operator || '--', 'Profiler Model', session?.profiler_model || '--'],
    ['Company', meta.company || '--', 'Serial #', meta.serial_num || '--'],
    ['Run Date', formatDate(session?.run_date), 'Certification #', meta.certification_num || '--'],
    ['Project', project?.name || '--', 'Cert. Date', meta.certification_date || '--'],
  ];

  infoData.forEach(([label1, val1, label2, val2]) => {
    const r = sheet.getRow(row);
    r.getCell(1).value = label1;
    r.getCell(1).font = { ...FONTS.normal, bold: true };
    r.getCell(2).value = val1;
    r.getCell(4).value = label2;
    r.getCell(4).font = { ...FONTS.normal, bold: true };
    r.getCell(5).value = val2;
    row++;
  });

  row += 2;

  // Station Info
  let startStation = session?.analysis_start_station || session?.start_station_ft || 0;
  let endStation = session?.analysis_end_station || session?.end_station_ft || 0;
  if (isReversed) [startStation, endStation] = [endStation, startStation];
  const totalDistance = Math.abs(endStation - startStation);

  sheet.getRow(row).getCell(1).value = 'Coverage';
  sheet.getRow(row).getCell(1).font = FONTS.heading;
  row++;

  const stationHeaders = sheet.getRow(row);
  stationHeaders.values = ['Start Station', 'End Station', 'Total Distance', 'Total Segments'];
  applyHeaderStyle(stationHeaders);
  row++;

  const stationData = sheet.getRow(row);
  stationData.values = [
    formatStation(startStation),
    formatStation(endStation),
    formatDistance(totalDistance),
    (segments || []).length
  ];
  applyDataRowStyle(stationData);
  row += 2;

  // IRI Statistics
  const iriValues = (segments || []).filter(s => s.iri_avg_in_per_mi != null).map(s => s.iri_avg_in_per_mi);
  const avgIRI = iriValues.length > 0 ? iriValues.reduce((a, b) => a + b, 0) / iriValues.length : null;
  const maxIRI = iriValues.length > 0 ? Math.max(...iriValues) : null;
  const minIRI = iriValues.length > 0 ? Math.min(...iriValues) : null;

  sheet.getRow(row).getCell(1).value = 'IRI Statistics';
  sheet.getRow(row).getCell(1).font = FONTS.heading;
  row++;

  const iriHeaders = sheet.getRow(row);
  iriHeaders.values = ['Metric', 'Average', 'Maximum', 'Minimum'];
  applyHeaderStyle(iriHeaders);
  row++;

  const iriData = sheet.getRow(row);
  iriData.values = [
    'Mean Roughness Index (MRI)',
    avgIRI !== null ? `${formatNumber(avgIRI)} in/mi` : '--',
    maxIRI !== null ? `${formatNumber(maxIRI)} in/mi` : '--',
    minIRI !== null ? `${formatNumber(minIRI)} in/mi` : '--'
  ];
  applyDataRowStyle(iriData);
  row += 2;

  // Threshold Analysis
  const threshold = projectConfig?.iri_threshold_type_a || 160;
  const segmentCount = (segments || []).length;
  const exceedingSegments = (segments || []).filter(s => s.iri_avg_in_per_mi > threshold).length;

  sheet.getRow(row).getCell(1).value = 'Threshold Analysis';
  sheet.getRow(row).getCell(1).font = FONTS.heading;
  row++;

  const threshHeaders = sheet.getRow(row);
  threshHeaders.values = ['IRI Threshold', 'Segments Exceeding', 'Percentage'];
  applyHeaderStyle(threshHeaders);
  row++;

  const threshData = sheet.getRow(row);
  threshData.values = [
    `${threshold} in/mi`,
    exceedingSegments,
    segmentCount > 0 ? `${((exceedingSegments / segmentCount) * 100).toFixed(1)}%` : '0%'
  ];
  applyDataRowStyle(threshData);
  if (exceedingSegments > 0) {
    threshData.eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.warnBg } };
    });
  }
  row += 2;

  // Event Summary
  const allEvents = events || [];
  const lrEvents = allEvents.filter(e => e.event_type === 'LR');
  const bumpEvents = allEvents.filter(e => e.event_type === 'Bump');
  const dipEvents = allEvents.filter(e => e.event_type === 'Dip');

  sheet.getRow(row).getCell(1).value = 'Event Summary';
  sheet.getRow(row).getCell(1).font = FONTS.heading;
  row++;

  const eventHeaders = sheet.getRow(row);
  eventHeaders.values = ['Event Type', 'Count', 'Status'];
  applyHeaderStyle(eventHeaders);
  row++;

  const eventData = [
    ['Localized Roughness (ALR)', lrEvents.length, lrEvents.length > 0 ? 'ATTENTION' : 'NONE'],
    ['Bumps', bumpEvents.length, bumpEvents.length > 0 ? 'DETECTED' : 'NONE'],
    ['Dips', dipEvents.length, dipEvents.length > 0 ? 'DETECTED' : 'NONE'],
  ];

  eventData.forEach(([type, count, status]) => {
    const r = sheet.getRow(row);
    r.values = [type, count, status];
    applyDataRowStyle(r);
    if (count > 0) {
      r.getCell(3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.warnBg } };
    } else {
      r.getCell(3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.passBg } };
    }
    row++;
  });

  setColumnWidths(sheet, [25, 20, 20, 20, 20]);
}

function generateSettingsSheet(workbook, session, projectConfig) {
  const sheet = workbook.addWorksheet('Settings');
  const meta = session?.meta || {};

  let row = addTitleRow(sheet, 'Collection Settings');
  row++;

  const headers = sheet.getRow(row);
  headers.values = ['Setting', 'Value', 'Unit'];
  applyHeaderStyle(headers);
  row++;

  const settings = [
    ['IRI Threshold (Type A)', formatNumber(projectConfig?.iri_threshold_type_a, 2), 'in/mi'],
    ['IRI Threshold (Type B)', formatNumber(projectConfig?.iri_threshold_type_b, 2), 'in/mi'],
    ['Reduction Length', meta['reduction_length(ft.)'] || '528', 'ft'],
    ['High-Pass Filter', meta['high-pass_filter(ft.)'] || '--', 'ft'],
    ['Low-Pass Filter', meta['low-pass_filter(ft.)'] || '--', 'ft'],
    ['Sample Rate', meta.sample_rate || '--', ''],
    ['Collection Speed', meta['collection_speed(mph)'] || '--', 'mph'],
  ];

  settings.forEach(([setting, value, unit]) => {
    const r = sheet.getRow(row);
    r.values = [setting, value, unit];
    applyDataRowStyle(r);
    r.getCell(2).alignment = { horizontal: 'right' };
    row++;
  });

  setColumnWidths(sheet, [30, 20, 15]);
}

function generateIRIIntervalsSheet(workbook, segments, projectConfig, isReversed) {
  const sheet = workbook.addWorksheet('IRI Intervals');
  const threshold = projectConfig?.iri_threshold_type_a || 160;

  let row = addTitleRow(sheet, 'IRI Intervals');
  sheet.getRow(row).getCell(1).value = `Threshold: ${threshold} in/mi`;
  sheet.getRow(row).getCell(1).font = FONTS.small;
  row += 2;

  const headers = sheet.getRow(row);
  headers.values = ['Start Station', 'End Station', 'Length', 'Left IRI', 'Right IRI', 'MRI'];
  applyHeaderStyle(headers);
  row++;

  const sortedSegments = [...(segments || [])].sort((a, b) =>
    isReversed ? b.from_station_ft - a.from_station_ft : a.from_station_ft - b.from_station_ft
  );

  sortedSegments.forEach(seg => {
    const startSta = isReversed ? seg.to_station_ft : seg.from_station_ft;
    const endSta = isReversed ? seg.from_station_ft : seg.to_station_ft;
    const mri = seg.iri_avg_in_per_mi;

    const r = sheet.getRow(row);
    r.values = [
      formatStation(startSta),
      formatStation(endSta),
      formatDistance(seg.distance_ft),
      formatNumber(seg.iri_left_in_per_mi),
      formatNumber(seg.iri_right_in_per_mi),
      formatNumber(mri)
    ];
    applyDataRowStyle(r);

    // Highlight exceeding segments
    if (mri > threshold) {
      r.eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.warnBg } };
      });
    }
    row++;
  });

  setColumnWidths(sheet, [15, 15, 12, 12, 12, 12]);
}

function generateALRSheet(workbook, events, projectConfig, isReversed) {
  const sheet = workbook.addWorksheet('ALR Events');
  const threshold = projectConfig?.iri_threshold_type_a || 160;

  let row = addTitleRow(sheet, 'Localized Roughness (ALR) Events');
  sheet.getRow(row).getCell(1).value = `Events exceeding IRI threshold of ${threshold} in/mi`;
  sheet.getRow(row).getCell(1).font = FONTS.small;
  row += 2;

  const lrEvents = (events || [])
    .filter(e => e.event_type === 'LR')
    .sort((a, b) => isReversed ? b.from_station_ft - a.from_station_ft : a.from_station_ft - b.from_station_ft);

  if (lrEvents.length === 0) {
    sheet.getRow(row).getCell(1).value = 'No localized roughness events detected.';
    sheet.getRow(row).getCell(1).font = FONTS.small;
    setColumnWidths(sheet, [40]);
    return;
  }

  // Statistics
  const magnitudes = lrEvents.map(e => e.magnitude_in).filter(m => m != null);
  const avgMag = magnitudes.length > 0 ? magnitudes.reduce((a, b) => a + b, 0) / magnitudes.length : null;
  const maxMag = magnitudes.length > 0 ? Math.max(...magnitudes) : null;

  sheet.getRow(row).getCell(1).value = `Total: ${lrEvents.length}`;
  sheet.getRow(row).getCell(2).value = `Avg: ${formatNumber(avgMag)} in/mi`;
  sheet.getRow(row).getCell(3).value = `Max: ${formatNumber(maxMag)} in/mi`;
  row += 2;

  const headers = sheet.getRow(row);
  headers.values = ['From Station', 'To Station', 'Wheelpath', 'IRI (in/mi)', 'Exceeds By'];
  applyHeaderStyle(headers);
  row++;

  lrEvents.forEach(evt => {
    const fromSta = isReversed ? evt.to_station_ft : evt.from_station_ft;
    const toSta = isReversed ? evt.from_station_ft : evt.to_station_ft;
    const exceedsBy = evt.magnitude_in ? evt.magnitude_in - threshold : null;

    const r = sheet.getRow(row);
    r.values = [
      formatStation(fromSta),
      formatStation(toSta),
      evt.wheelpath || '--',
      formatNumber(evt.magnitude_in),
      exceedsBy > 0 ? `+${formatNumber(exceedsBy)}` : '--'
    ];
    applyDataRowStyle(r);

    if (exceedsBy > 100) {
      r.getCell(5).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.failBg } };
    } else if (exceedsBy > 0) {
      r.getCell(5).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.warnBg } };
    }
    row++;
  });

  setColumnWidths(sheet, [15, 15, 12, 15, 15]);
}

function generateRollingStraitedgeSheet(workbook, events, isReversed) {
  const sheet = workbook.addWorksheet('Rolling Straightedge');

  const sortFn = (a, b) => isReversed ? b.from_station_ft - a.from_station_ft : a.from_station_ft - b.from_station_ft;
  const bumps = (events || []).filter(e => e.event_type === 'Bump').sort(sortFn);
  const dips = (events || []).filter(e => e.event_type === 'Dip').sort(sortFn);

  let row = addTitleRow(sheet, 'Rolling Straightedge Events');
  row++;

  // Bumps Section
  sheet.getRow(row).getCell(1).value = `Bumps (${bumps.length})`;
  sheet.getRow(row).getCell(1).font = FONTS.heading;
  row++;

  if (bumps.length === 0) {
    sheet.getRow(row).getCell(1).value = 'No bumps detected.';
    sheet.getRow(row).getCell(1).font = FONTS.small;
    row += 2;
  } else {
    const bumpHeaders = sheet.getRow(row);
    bumpHeaders.values = ['Location', 'Wheelpath', 'Height (in)', 'Peak Station'];
    applyHeaderStyle(bumpHeaders);
    row++;

    bumps.forEach(bump => {
      const location = isReversed ? bump.to_station_ft : bump.from_station_ft;
      const r = sheet.getRow(row);
      r.values = [
        formatStation(location),
        bump.wheelpath || '--',
        formatNumber(bump.magnitude_in, 3),
        formatStation(bump.peak_station_ft)
      ];
      applyDataRowStyle(r);
      row++;
    });
    row++;
  }

  // Dips Section
  sheet.getRow(row).getCell(1).value = `Dips (${dips.length})`;
  sheet.getRow(row).getCell(1).font = FONTS.heading;
  row++;

  if (dips.length === 0) {
    sheet.getRow(row).getCell(1).value = 'No dips detected.';
    sheet.getRow(row).getCell(1).font = FONTS.small;
  } else {
    const dipHeaders = sheet.getRow(row);
    dipHeaders.values = ['Location', 'Wheelpath', 'Depth (in)', 'Peak Station'];
    applyHeaderStyle(dipHeaders);
    row++;

    dips.forEach(dip => {
      const location = isReversed ? dip.to_station_ft : dip.from_station_ft;
      const r = sheet.getRow(row);
      r.values = [
        formatStation(location),
        dip.wheelpath || '--',
        formatNumber(dip.magnitude_in, 3),
        formatStation(dip.peak_station_ft)
      ];
      applyDataRowStyle(r);
      row++;
    });
  }

  setColumnWidths(sheet, [15, 12, 15, 15]);
}

// ============================================================================
// MAIN EXPORT FUNCTION
// ============================================================================

export async function generateSessionReportXLSX(options) {
  const {
    session,
    project,
    projectConfig,
    segments = [],
    events = [],
    sections = {
      summary: true,
      settings: true,
      iriIntervals: true,
      alr: true,
      rollingStraitedge: true,
    }
  } = options;

  const isReversed = session?.meta?.is_reverse_stationing === true;
  const workbook = new ExcelJS.Workbook();

  workbook.creator = 'Ames Connect';
  workbook.created = new Date();
  workbook.modified = new Date();

  if (sections.summary) {
    generateSummarySheet(workbook, session, project, projectConfig, segments, events, isReversed);
  }

  if (sections.settings) {
    generateSettingsSheet(workbook, session, projectConfig);
  }

  if (sections.iriIntervals) {
    generateIRIIntervalsSheet(workbook, segments, projectConfig, isReversed);
  }

  if (sections.alr) {
    generateALRSheet(workbook, events, projectConfig, isReversed);
  }

  if (sections.rollingStraitedge) {
    generateRollingStraitedgeSheet(workbook, events, isReversed);
  }

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
}

export function getXLSXReportFilename(session) {
  const sessionName = (session?.name || session?.filename || 'session')
    .replace(/[^a-zA-Z0-9-_]/g, '_')
    .substring(0, 50);
  return `${sessionName}_Report.xlsx`;
}

export default generateSessionReportXLSX;